import useSWR from "swr";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import type { LinkedBoard } from "../table.data.interface";
import type { BoardColumnsById } from "./shareColumnEligibility";

interface ShareColumnBoards {
  columnsByBoardId: BoardColumnsById;
  /** boardId -> monday access level. A board missing here is treated as editable. */
  accessByBoardId: Record<string, string>;
}

const fetchShareColumnBoards = async (linkedBoards: LinkedBoard[]): Promise<ShareColumnBoards> => {
  const boardIds = linkedBoards.map((board) => parseInt(board.boardId, 10));

  const [boards, accessLevels] = await Promise.all([
    MondayApiQueryInstance.getBoardColumns(boardIds),
    // An access lookup that fails must not block the dialog: monday still refuses the write.
    Promise.all(
      boardIds.map((boardId) => MondayApiQueryInstance.getAccessLevel([boardId]).catch(() => "")),
    ),
  ]);

  const columnsByBoardId: BoardColumnsById = {};
  (boards ?? []).forEach((board: any) => {
    columnsByBoardId[String(board.id)] = board.columns ?? [];
  });

  const accessByBoardId: Record<string, string> = {};
  linkedBoards.forEach((board, index) => {
    accessByBoardId[board.boardId] = accessLevels[index] ?? "";
  });

  return { columnsByBoardId, accessByBoardId };
};

/**
 * Live columns and access level for every linked board, loaded when the share dialog opens.
 * The item view's own data only carries supported column *values*; deciding which boards lack
 * a column, and whether a name is free, needs each board's actual column list.
 */
const useShareColumnBoards = (linkedBoards: LinkedBoard[], enabled: boolean) => {
  const key = enabled
    ? `shareColumnBoards:${linkedBoards.map((board) => board.boardId).join(",")}`
    : null;

  return useSWR<ShareColumnBoards>(key, () => fetchShareColumnBoards(linkedBoards), {
    revalidateOnFocus: false,
    // Always refetch on open: the dialog's whole job is to act on the boards as they are now.
    dedupingInterval: 0,
  });
};

export default useShareColumnBoards;
